import { Box, Icon, Menu, MenuItem, SxProps, Theme } from "@outschool/backpack";
import { faGear, faMessage } from "@outschool/icons";
import { useLocale, useTranslation } from "@outschool/localization";
import { enrolledPath, sectionEnrollPath } from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import React from "react";
import { useLocation } from "react-router";

import { shortenedLeaderName } from "../../../shared/User";
import { WaitlistedClassItem } from "../../hooks/useWaitlistedClasses";
import Link from "../Link";
// @ts-ignore - JSX import
import MessageModal from "../MessageModal";
import LeaveWaitlistButton from "../waitlists/LeaveWaitlistButton";
import ClassItemActionButton from "./ClassItemActionButton";

enum WaitlistControl {
  EnrollInSection,
  WithdrawFromWaitlist,
  ManageWaitlist,
}

const BUTTON_INHERIT_SX: SxProps = {
  width: "100%",
  color: "inherit",
  textDecoration: "none !important",
  fontSize: "inherit",
  backgroundColor: "transparent",
  "&:hover:not(:disabled), &:focus:not(:disabled)": {
    backgroundColor: "rgba(0, 0, 0, 0.04) !important",
    color: "inherit !important",
  },
};

interface Props {
  item: WaitlistedClassItem;
}

const WaitlistedClassItemActions = ({ item }: Props) => {
  const { t } = useTranslation("client\\components\\WaitlistedClassList");
  const location = useLocation();
  const { currentUser } = useSession();
  const locale = useLocale();

  const isNewParentDashboardEnabled = useFeatureFlag("new-parent-dashboard-v1");

  const moreButtonRef = React.useRef<HTMLButtonElement>(null);
  const [showMore, setShowMore] = React.useState(false);

  const { section } = item;
  const { activity, leader } = section;

  const iconColor = "primary.main";

  const menuItems = React.useMemo(() => {
    if (activity.hasTeacherSchedule) {
      return [
        <MenuItem
          component={Link}
          to={enrolledPath(section.uid, locale)}
          sx={BUTTON_INHERIT_SX}
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
          }}
          key="manage-meetings"
        >
          {t("Manage meetings")}
        </MenuItem>,
      ];
    }

    const controlsToDisplay = !!item.availableSpaces
      ? [WaitlistControl.EnrollInSection, WaitlistControl.WithdrawFromWaitlist]
      : [WaitlistControl.ManageWaitlist];

    return controlsToDisplay.map(control => {
      if (control === WaitlistControl.EnrollInSection) {
        return (
          <MenuItem
            key={control}
            component={Link}
            to={sectionEnrollPath(activity, item.waitlistSeats[0]?.section)}
            target="_blank"
            variant="link"
            color="inherit"
            onClick={(e: any) => e.stopPropagation()}
            sx={BUTTON_INHERIT_SX}
          >
            {t("Enroll in section")}
          </MenuItem>
        );
      } else {
        return (
          <MenuItem
            key={control}
            component={LeaveWaitlistButton as any}
            learner={item.learner}
            activity={activity}
            waitlistSeats={item.waitlistSeats}
            title={
              control === WaitlistControl.ManageWaitlist
                ? t("Manage waitlist")
                : t("Withdraw from waitlist(s)")
            }
            variant="link"
            color="inherit"
            onClick={(e: any) => e.stopPropagation()}
            sx={BUTTON_INHERIT_SX}
          />
        );
      }
    });
  }, [activity, item, section, locale, t]);

  return (
    <Box
      flex
      sx={(theme: Theme) => ({
        flexDirection: "row",
        gap: 20,
        minWidth: "18rem",
        justifyContent: "end",
        [theme.breakpoints.down(isNewParentDashboardEnabled ? "lg" : "md")]: {
          width: "100%",
          justifyContent: "space-evenly",
        },
      })}
    >
      <MessageModal
        renderButton={(open: () => void) => (
          <ClassItemActionButton
            icon={<Icon icon={faMessage} fill={iconColor} />}
            text={t("Message")}
            onClick={open}
          />
        )}
        user={currentUser}
        location={location}
        requireLogin={true}
        isLoggedIn
        viewName="messageToLeader"
        heading={t("Message to {{teacherName}}", {
          teacherName: shortenedLeaderName(leader.name),
        })}
        recipients={[leader]}
        activityUid={activity.uid}
        sectionUid={section && section.uid}
        showFromAndTo
        subject={t("Message about: {{activityTitle}}", {
          activityTitle: activity.title,
        })}
        context={{
          action: "Enrolled parent to teacher",
          activityTitle: activity.title,
          subject: "Message about: " + activity.title,
        }}
      />
      <ClassItemActionButton
        ref={moreButtonRef}
        icon={<Icon icon={faGear} fill={iconColor} />}
        text={t("Manage")}
        onClick={e => {
          e.preventDefault();
          setShowMore(!showMore);
        }}
      />
      <Menu
        anchorEl={moreButtonRef.current}
        open={showMore}
        onClose={(e: React.MouseEvent<HTMLElement>) => {
          e.stopPropagation();
          setShowMore(false);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {menuItems}
      </Menu>
    </Box>
  );
};

export default WaitlistedClassItemActions;
